import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

type LayoutMode = 'classic' | 'sidebar';

interface WorkspaceContextValue {
    layoutMode: LayoutMode;
    isSidebarLayout: boolean;
    toggleLayoutMode: () => void;
}

const STORAGE_KEY = 'aliro_layout_mode';

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
    const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored === 'classic' ? 'classic' : 'sidebar';
    });

    const toggleLayoutMode = useCallback(() => {
        setLayoutMode((prev) => {
            const next = prev === 'classic' ? 'sidebar' : 'classic';
            localStorage.setItem(STORAGE_KEY, next);
            return next;
        });
    }, []);

    const value = useMemo<WorkspaceContextValue>(
        () => ({
            layoutMode,
            isSidebarLayout: layoutMode === 'sidebar',
            toggleLayoutMode,
        }),
        [layoutMode, toggleLayoutMode]
    );

    return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspace = () => {
    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
    return ctx;
};
